import { Bookmark, ChevronLeft, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'wouter';
import { StyleCard } from '@/components/StyleCard';
import { StyleVisual } from '@/components/StyleVisual';
import { styleColorHex, styles, type TurbanStyle } from '@/data/styles';

const BROWSE_GROUPS = [
  {
    id: 'punjabi',
    title: 'Punjabi Styles',
    description: 'Regional silhouettes with distinct front geometry, from broad statement fans to neat daily folds.',
    styleIds: ['patiala-shahi', 'amritsari', 'morni', 'nok', 'malwai', 'barnala'],
  },
  {
    id: 'traditional',
    title: 'Traditional Sikh',
    description: 'Grounded forms built around heritage, practice, and the unmistakable architecture of the dastaar.',
    styleIds: ['dumalla', 'gol-dastar', 'keski'],
  },
  {
    id: 'wedding',
    title: 'Wedding',
    description: 'Ceremony-ready silhouettes with a little more lift, structure, and presence.',
    styleIds: ['punjabi-groom', 'royal-groom', 'classic-wedding', 'modern-wedding'],
  },
  {
    id: 'casual',
    title: 'Casual',
    description: 'Light, practical wraps for the days when comfort and character should coexist.',
    styleIds: ['parna', 'wattan-wali-pagg', 'double-patti', 'uk-style', 'kenyan-style'],
  },
];

export default function Styles() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [selected, setSelected] = useState<TurbanStyle | null>(null);
  const [detailColour, setDetailColour] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const filters = ['All', 'Punjabi Styles', 'Traditional Sikh', 'Wedding', 'Casual', 'Saved'];

  useEffect(() => {
    const refresh = () => setSavedTick((value) => value + 1);
    window.addEventListener('turbans-favourites-changed', refresh);
    return () => window.removeEventListener('turbans-favourites-changed', refresh);
  }, []);

  useEffect(() => {
    if (!selected) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusTimer = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelected(null);
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [selected]);

  const savedIds = useMemo(() => {
    if (typeof window === 'undefined') return [];
    // Dependency on savedTick forces this to re-evaluate when saved items change
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _tick = savedTick;
    return JSON.parse(localStorage.getItem('turbans-favourites') || '[]') as string[];
  }, [savedTick]);

  const isSearchingOrFiltering = query.trim() !== '' || filter !== 'All';

  const visibleStyles = useMemo(() => {
    return styles.filter((style) => {
      const matchesQuery = `${style.name} ${style.tags.join(' ')} ${style.region}`
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesFilter =
        filter === 'All' ||
        (filter === 'Saved'
          ? savedIds.includes(style.id)
          : style.category === filter);
      return matchesQuery && matchesFilter;
    });
  }, [query, filter, savedIds]);

  return (
    <div className="page-enter min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0c1421] px-5 pb-16 pt-16 text-white sm:px-8 lg:px-12 lg:pb-24 lg:pt-20">
        <div className="absolute inset-0 opacity-20 grain" />
        <div className="absolute -right-[20%] -top-[40%] h-[800px] w-[800px] rounded-full bg-brand-gradient opacity-10 blur-[120px]" />

        <div className="relative mx-auto max-w-[1320px]">
          <div className="flex items-center gap-2 mono-font text-[11px] uppercase tracking-[.2em] text-[#ff9340]">
            <Link
              href="/"
              className="hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9340] rounded-sm"
              data-testid="link-breadcrumb-home"
            >
              Discover
            </Link>
            <ChevronLeft size={14} className="rotate-180" />
            <span className="text-white/60">Style library</span>
          </div>

          <div className="mt-10 grid gap-10 md:grid-cols-[1.5fr_1fr] md:items-end lg:gap-16">
            <div>
              <h1 className="display-font text-5xl leading-[0.9] tracking-[-.04em] sm:text-7xl lg:text-[5.5rem]">
                The <em className="not-italic text-[#ff744f]">dastaar</em>
                <br />
                edit.
              </h1>
              <p className="mt-6 max-w-[480px] text-lg leading-relaxed text-white/70 font-medium">
                 Eighteen silhouettes worth knowing. Browse by region, tradition, ceremony, or daily rhythm.
              </p>
            </div>

            <div className="relative w-full max-w-md md:ml-auto">
              <label htmlFor="search-styles" className="sr-only">Search the library</label>
              <Search
                className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40"
                size={18}
              />
              <input
                id="search-styles"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search styles, occasions, or tags..."
                className="h-14 w-full rounded-full border border-white/15 bg-white/5 pl-12 pr-5 text-sm text-white placeholder-white/40 outline-none backdrop-blur-md transition-all focus:border-[#ff9340] focus:bg-white/10 focus:ring-4 focus:ring-[#ff9340]/20"
                data-testid="input-search-styles"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="mx-auto max-w-[1320px] px-5 py-12 sm:px-8 lg:px-12 lg:py-16">
        {/* Filters */}
        <div className="mb-12 flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Style filters">
            {filters.map((item) => {
              const isActive = filter === item;
              return (
                <button
                  key={item}
                  onClick={() => setFilter(item)}
                  className={`flex items-center rounded-full px-5 py-2.5 text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'border border-border/80 bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground hover:shadow-sm'
                  }`}
                  data-testid={`button-filter-${item.toLowerCase()}`}
                  aria-pressed={isActive}
                >
                  {item === 'Saved' && (
                    <Bookmark
                      className="mr-2 inline"
                      size={14}
                      fill={isActive ? 'currentColor' : 'none'}
                    />
                  )}
                  {item}
                </button>
              );
            })}
          </div>
          {isSearchingOrFiltering && (
            <span className="mono-font text-[11px] uppercase tracking-[.15em] text-muted-foreground font-medium">
              {visibleStyles.length} {visibleStyles.length === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>

        {/* View Mode: Filtered/Searched vs Grouped */}
        {isSearchingOrFiltering ? (
          /* Flat list for search/filter results */
          visibleStyles.length > 0 ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 rise-in">
              {visibleStyles.map((style) => (
                <StyleCard key={style.id} style={style} onOpen={(item) => { setSelected(item); setDetailColour(null); }} />
              ))}
            </div>
          ) : (
            <div
              className="flex min-h-[360px] flex-col items-center justify-center rounded-[2rem] border border-dashed border-border/80 bg-card/40 px-6 text-center"
              data-testid="empty-style-results"
            >
              <div className="mb-5 rounded-full bg-muted p-5 text-muted-foreground">
                <Search size={26} />
              </div>
              <h2 className="display-font text-3xl text-foreground">Nothing tied up here.</h2>
              <p className="mt-3 max-w-[340px] text-base leading-relaxed text-muted-foreground">
                Try another search term, or clear the filter to explore the full collection.
              </p>
              <button
                onClick={() => {
                  setQuery('');
                  setFilter('All');
                }}
                className="mt-6 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                data-testid="button-clear-style-filters"
              >
                Clear all filters
              </button>
            </div>
          )
        ) : (
          /* Grouped list for default view */
          <div className="space-y-20">
            {BROWSE_GROUPS.map((group, groupIdx) => {
              const groupStyles = group.styleIds
                .map((id) => styles.find((s) => s.id === id))
                .filter((s): s is TurbanStyle => Boolean(s));

              if (groupStyles.length === 0) return null;

              return (
                <div key={group.id} className={`rise-in delay-${(groupIdx % 4) + 1}`}>
                  <div className="mb-8 max-w-2xl">
                    <h2 className="display-font text-3xl text-foreground sm:text-4xl">
                      {group.title}
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                      {group.description}
                    </p>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {groupStyles.map((style, idx) => (
                      <StyleCard
                        key={style.id}
                        style={style}
                        onOpen={(item) => { setSelected(item); setDetailColour(null); }}
                        featured={groupIdx === 0 && idx === 0} // Feature the first traditional style
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Detail Dialog */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 backdrop-blur-md sm:items-center sm:p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          data-testid="dialog-style-detail"
          onClick={() => setSelected(null)}
        >
          <div
            ref={dialogRef}
            className="relative max-h-[92dvh] w-full max-w-[720px] overflow-y-auto rounded-t-[2rem] bg-background shadow-2xl sm:rounded-[2rem] page-enter"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              ref={closeButtonRef}
              onClick={() => setSelected(null)}
              className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/20 text-white backdrop-blur-md transition-colors hover:bg-black/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Close style detail"
              data-testid="button-close-style-detail"
            >
              <X size={20} />
            </button>
            <div className="grid sm:grid-cols-[1fr_1.3fr]">
              <div
                className="relative min-h-[300px] overflow-hidden bg-primary sm:min-h-full"
                style={{
                  background: `linear-gradient(135deg, ${selected.accent} 0%, #152436 100%)`,
                }}
              >
                <StyleVisual
                  style={selected}
                  className="absolute inset-0"
                  imageClassName="absolute inset-0 h-full w-full object-cover object-[center_25%]"
                  imageOverride={detailColour ? selected.colorImages[detailColour] : undefined}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#101c2d]/80 via-transparent to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-black/30" />
              </div>

              <div className="p-8 sm:p-10 flex flex-col h-full">
                <div>
                  <p className="mono-font text-[11px] uppercase tracking-[.2em] text-primary font-semibold">
                    {selected.region}
                  </p>
                  <h2 id="dialog-title" className="display-font mt-4 text-4xl leading-[1.1] sm:text-5xl text-foreground">
                    {selected.name}
                  </h2>
                  <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                    {selected.description}
                  </p>
                  <div className="mt-6">
                    <p className="mono-font text-[10px] uppercase tracking-[.15em] text-muted-foreground">Preview a colour</p>
                    <div className="mt-3 flex flex-wrap gap-2.5">
                      {selected.colors.map((colour) => (
                        <button
                          key={colour}
                          type="button"
                          onClick={() => setDetailColour(colour)}
                          className={`flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold capitalize transition ${detailColour === colour ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-foreground hover:border-primary/50'}`}
                          aria-pressed={detailColour === colour}
                          data-testid={`detail-preview-${selected.id}-${colour.replaceAll(' ', '-')}`}
                        >
                          <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: styleColorHex[colour] ?? selected.accent }} />
                          {colour}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8 border-t border-border/80 pt-6 flex-grow">
                  <h3 className="text-[11px] font-bold uppercase tracking-[.15em] text-foreground">
                    The Silhouette
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {selected.visualSpec}
                  </p>
                   <div className="mt-6 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                     {[
                       ['Front', selected.geometry.frontGeometry],
                       ['Folds', selected.geometry.foldDirection],
                       ['Crown', selected.geometry.crownHeight],
                       ['Forehead', selected.geometry.foreheadPosition],
                     ].map(([label, value]) => (
                       <div key={label} className="rounded-xl bg-muted/70 px-3 py-2.5">
                         <p className="mono-font text-[9px] uppercase tracking-[.12em] text-muted-foreground">{label}</p>
                         <p className="mt-1 text-[11px] font-semibold capitalize leading-tight text-foreground">{value}</p>
                       </div>
                     ))}
                   </div>

                  <h3 className="mt-6 text-[11px] font-bold uppercase tracking-[.15em] text-foreground">
                    Best For
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selected.bestFor}
                  </p>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {selected.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-secondary/10 text-secondary-foreground px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[.1em]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8 pt-2">
                  <button
                    onClick={() => {
                      const current: string[] = JSON.parse(
                        localStorage.getItem('turbans-favourites') || '[]'
                      );
                      const isCurrentlySaved = current.includes(selected.id);
                      const next = isCurrentlySaved
                        ? current.filter((id) => id !== selected.id)
                        : [...current, selected.id];
                      localStorage.setItem('turbans-favourites', JSON.stringify(next));
                      window.dispatchEvent(new Event('turbans-favourites-changed'));
                    }}
                    className={`flex w-full items-center justify-center gap-2.5 rounded-full px-6 py-4 text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                      savedIds.includes(selected.id)
                        ? 'bg-muted text-foreground hover:bg-border/60 focus-visible:ring-muted'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-primary shadow-lg hover:shadow-xl'
                    }`}
                    data-testid={`button-detail-save-${selected.id}`}
                  >
                    <Bookmark
                      size={18}
                      fill={savedIds.includes(selected.id) ? 'currentColor' : 'none'}
                    />
                    {savedIds.includes(selected.id) ? 'Saved to library' : 'Save this style'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}